import { cn } from '@/lib/utils'

type PageHeaderProps = {
  readonly title: string
  readonly subtitle?: string
  /**
   * Small mono uppercase label that sits above the title, marketing-site
   * style. Rendered with or without the `// ` prefix depending on what's
   * passed. Default: derived from the title.
   */
  readonly eyebrow?: string
  readonly actions?: React.ReactNode
  readonly className?: string
}

/**
 * Page header — the single repeated pattern across every page.
 *
 * Visual language intentionally matches dealcloserautopilot.com:
 *   - mono eyebrow in cobalt-bright ("// LEADS" etc.)
 *   - larger serif-adjacent tracking-tight title
 *   - silver subhead with generous line-height
 *   - right-aligned actions with vertical-center alignment
 *
 * Stays a dumb presentational component — no route awareness, just content.
 */
export function PageHeader({ title, subtitle, eyebrow, actions, className }: PageHeaderProps) {
  const resolvedEyebrow = eyebrow ?? `// ${title.toUpperCase()}`

  return (
    <div
      className={cn(
        'flex flex-col gap-5 sm:flex-row sm:items-start sm:justify-between sm:gap-6',
        className,
      )}
    >
      <div className="flex min-w-0 flex-col gap-2">
        <span className="eyebrow">{resolvedEyebrow}</span>
        <h1 className="text-3xl font-bold tracking-tight text-[var(--color-silver-bright)] md:text-[2rem]">
          {title}
        </h1>
        {subtitle && (
          <p className="max-w-2xl text-sm leading-relaxed text-[var(--color-silver)]">
            {subtitle}
          </p>
        )}
      </div>
      {actions && (
        <div className="flex flex-wrap items-center gap-2 sm:shrink-0 sm:justify-end sm:pt-1">{actions}</div>
      )}
    </div>
  )
}
